'use client';

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.tabIndex !== -1,
  );
}

interface Options {
  /** When false the trap detaches and focus is released. */
  active?: boolean;
  /** Escape handler. Omit to leave Escape to the caller. */
  onEscape?: () => void;
  /**
   * Focus target when the trap engages. Receives the container so a caller can
   * prefer a specific control; return null to fall back to the first focusable
   * element. Runs a frame late so portaled content is mounted.
   */
  initialFocus?: (root: HTMLElement) => HTMLElement | null | undefined;
  /** Set false to leave focus where it is on teardown. */
  restoreFocus?: boolean;
}

/**
 * Confines Tab cycling to `ref` and restores focus to whatever was focused
 * before the trap engaged. Extracted from Modal, which is still its largest
 * consumer — an overlay that does not do this leaves a keyboard user tabbing
 * through the page behind it with no visible cursor.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  { active = true, onEscape, initialFocus, restoreFocus = true }: Options = {},
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusRaf = requestAnimationFrame(() => {
      const preferred = initialFocus?.(root) ?? getFocusableElements(root)[0];
      preferred?.focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(root);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement;

      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(focusRaf);
      document.removeEventListener('keydown', onKeyDown);
      if (restoreFocus) previouslyFocused?.focus?.();
    };
  }, [active, ref, onEscape, restoreFocus]);
}
